import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import {
    Box,
    IconButton,
    TableBody,
    TableRow,
    Tooltip,
    Typography,
    styled,
    useTheme
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';

import ContentContainer from '@libComponents/ContentContainer';
import FilterInput from '@libComponents/FilterInput';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import TableCellContainer from '@libComponents/table/TableCellContainer';
import { Pagination } from '@libComponents';
import { SelectBase } from '@baseComponents/Select';
import {
    createFetchPaginatedDataset,
    datasetLoadingSelector,
    entitySelector
} from '@libs/datasets';
import { usePermission } from 'permissions/hooks';

import {
    DOMAIN_NAME,
    ROWS_PER_PAGE,
    STATUS,
    STATUS_FILTER_OPTIONS
} from '../common';
import { ALL_CHANGE_REQUESTS } from '../redux.datasets';
import ChangeRequestDetailsDialog from './ChangeRequestDetailsDialog';
import StatusChip from './StatusChip';

// Same table shell the leave-management list uses: full width, scrollable,
// fixed column layout, subtle row hover.
const StyledBox = styled(Box)(({ theme }) => ({
    width: '100%',
    overflow: 'auto',
    '& table': { tableLayout: 'fixed' },
    '& .MuiTableRow-root': {
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: theme.palette.action.hover }
    }
}));

const TABLE_COLUMNS = {
    employeeEmail: { label: 'Employee', width: '22%' },
    createdAt: { label: 'Requested On', width: '16%' },
    changes: { label: 'Requested Changes', width: '30%' },
    status: { label: 'Status', width: '12%' },
    updatedBy: { label: 'Reviewed By', width: '12%' }
};

const defaultProps = { entity: ALL_CHANGE_REQUESTS, domain: DOMAIN_NAME };

const dataSelector = createSelector(
    (state) => entitySelector(state, DOMAIN_NAME, ALL_CHANGE_REQUESTS),
    (resp) => (resp && resp.data ? resp.data : [])
);

const totalRowsSelector = createCachedSelector(entitySelector, (resp) =>
    resp && resp.total ? parseInt(resp.total) : 0
)((state, domain, entity) => `@data-${domain}-${entity}`);

const mapState = (state, { domain, entity }) => ({
    data: dataSelector(state, domain, entity),
    loading: datasetLoadingSelector(state, domain, entity),
    totalRows: totalRowsSelector(state, domain, entity)
});

const mapDispatch = (dispatch, { entity }) => ({
    fetchDataset: ({ offset = 0, limit = ROWS_PER_PAGE, filter = {} } = {}) =>
        dispatch(
            createFetchPaginatedDataset({ entity, domain: DOMAIN_NAME })({
                limit,
                offset,
                shouldAppend: false,
                'sort-descending': true,
                filterKey: filter
            })
        )
});

/**
 * Admin — every employee profile change request. Defaults to the pending
 * queue; opening a pending row starts the per-field review, decided rows
 * open read-only (that is the request history).
 */
const ChangeRequestsAdmin = connect(
    mapState,
    mapDispatch
)(({ data = [], totalRows = 0, fetchDataset }) => {
    const theme = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(ROWS_PER_PAGE);
    const [statusFilter, setStatusFilter] = useState(STATUS.pending);
    const [searchTerm, setSearchTerm] = useState('');
    const [selected, setSelected] = useState(null);

    const canReview = usePermission(
        'profile-change-request.review a profile change request'
    );

    const buildFilter = useCallback(
        () => ({
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(searchTerm ? { searchTerm } : {})
        }),
        [statusFilter, searchTerm]
    );

    useEffect(() => {
        setCurrentPage(1);
        fetchDataset({ offset: 0, limit: pageSize, filter: buildFilter() });
    }, [fetchDataset, buildFilter, pageSize]);

    const refetch = (page = currentPage) =>
        fetchDataset({
            offset: (page - 1) * pageSize,
            limit: pageSize,
            filter: buildFilter()
        });

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
        refetch(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setPageSize(event.target.value);
        setCurrentPage(1);
    };

    const summarizeChanges = (changes = []) =>
        changes.map((change) => change.label).join(', ');

    return (
        <ContentContainer
            title='Profile Change Requests'
            headerBg={theme.palette.background.mainHeader}
            AdornmentComponent={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'nowrap'
                    }}
                >
                    <FilterInput
                        name='change-request-search'
                        placeholder='Search by email'
                        handleSearch={(value) => setSearchTerm(value)}
                    />
                    <SelectBase
                        fieldName='change-request-status'
                        placeholder='Select Status'
                        meta={{ form: 'change-request-status' }}
                        data={STATUS_FILTER_OPTIONS}
                        sx={{
                            width: 200,
                            mt: 1,
                            backgroundColor: theme.palette.background.white
                        }}
                        input={{
                            name: 'status',
                            value: statusFilter,
                            onChange: (value) => setStatusFilter(value)
                        }}
                    />
                </Box>
            }
        >
            <StyledBox>
                <TableContainer>
                    <TableHeaderContainer>
                        {Object.entries(TABLE_COLUMNS).map(
                            ([key, { label, width }], index) => (
                                <TableCellContainer
                                    key={key}
                                    typography='caption'
                                    sx={{
                                        pl: index === 0 ? 4 : 2,
                                        width,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {label}
                                </TableCellContainer>
                            )
                        )}
                        <TableCellContainer
                            typography='caption'
                            align='right'
                            sx={{ pr: 4, width: '8%', fontWeight: 'bold' }}
                        >
                            Action
                        </TableCellContainer>
                    </TableHeaderContainer>
                    <TableBody>
                        {data.map((row) => (
                            <TableRow
                                key={row.id}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => setSelected(row)}
                            >
                                <TableCellContainer sx={{ pl: 4 }}>
                                    <Typography variant='body2' noWrap>
                                        {row.employeeEmail}
                                    </Typography>
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    {dayjs(row.createdAt).format('MMM DD, YYYY (ddd)')}
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <Tooltip
                                        title={summarizeChanges(row.changes)}
                                        arrow
                                        placement='top'
                                    >
                                        <Typography variant='body2' noWrap>
                                            {`${row.changes?.length || 0} field${
                                                (row.changes?.length || 0) > 1 ? 's' : ''
                                            } — ${summarizeChanges(row.changes)}`}
                                        </Typography>
                                    </Tooltip>
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <StatusChip status={row.status} />
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <Typography variant='body2' noWrap>
                                        {row.updatedBy || '—'}
                                    </Typography>
                                </TableCellContainer>
                                <TableCellContainer
                                    align='right'
                                    sx={{ pr: 4 }}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <Tooltip
                                        title={
                                            row.status === STATUS.pending && canReview
                                                ? 'Review'
                                                : 'View details'
                                        }
                                        arrow
                                        placement='top'
                                    >
                                        <IconButton
                                            size='small'
                                            color='primary'
                                            onClick={() => setSelected(row)}
                                        >
                                            <VisibilityIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </TableCellContainer>
                            </TableRow>
                        ))}
                        {!data.length && (
                            <TableRow>
                                <TableCellContainer
                                    colSpan={6}
                                    align='center'
                                    sx={{ py: 6, color: 'text.secondary' }}
                                >
                                    No change requests found for the selected filters.
                                </TableCellContainer>
                            </TableRow>
                        )}
                    </TableBody>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={Math.max(1, Math.ceil(totalRows / pageSize))}
                        page={currentPage}
                        onChange={handleChangePage}
                        sx={{ display: 'flex', justifyContent: 'center' }}
                        isRowsPerPageVarient
                        rowsPerPageProps={{
                            name: 'changeRequests',
                            onChange: handleRowsPerPageChange,
                            value: pageSize
                        }}
                    />
                </Box>
            </StyledBox>

            <ChangeRequestDetailsDialog
                open={!!selected}
                onClose={() => setSelected(null)}
                request={selected}
                mode={canReview ? 'review' : 'view'}
                onReviewed={() => refetch()}
            />
        </ContentContainer>
    );
});

ChangeRequestsAdmin.defaultProps = defaultProps;

export default ChangeRequestsAdmin;
